import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Radio,
    RadioGroup,
    IconButton,
    styled,
    MenuItem
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Tooltip from '@mui/material/Tooltip';
import api from '../services/api';
import { userQuestionnaireService } from '../services/questionnaireService';
import { useAuthStore } from '../stores/authStore';
import AiHelperWidget from '../components/AiHelperWidget';

// Types matching backend
interface Field {
    id: string;
    label: string;
    type: string;
    required: boolean;
    helperText?: string;
    options?: string[];
    aiHelper?: string;
    placeholder?: string;
    tooltip?: string;
}

interface StepDef {
    section: string;
    title: string;
    description?: string;
    fields: Field[];
}

interface QuestionnaireDefinition {
    _id?: string;
    id?: string;
    version: number;
    steps: StepDef[];
    isAssigned?: boolean;
    assignmentId?: string;
    answers?: Record<string, unknown>;
    title?: string;
    description?: string;
}

// Styled Components for Airbnb-like design
const PageContainer = styled(Box)({
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
});

const Header = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 40px',
    borderBottom: '1px solid #ebebeb',
    position: 'sticky',
    top: 0,
    backgroundColor: '#ffffff',
    zIndex: 100,
});

const Logo = styled(Typography)({
    fontSize: '24px',
    fontWeight: 700,
    color: '#222222',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
});

const HeaderActions = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
});

const HeaderButton = styled(Button)({
    borderRadius: '20px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    padding: '8px 16px',
    color: '#222222',
    border: '1px solid #222222',
    '&:hover': {
        backgroundColor: '#f7f7f7',
        border: '1px solid #222222',
    },
});

const MainContent = styled(Box)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '60px 40px 120px',
    maxWidth: '640px',
    margin: '0 auto',
    width: '100%',
});

const PageTitle = styled(Typography)({
    fontSize: '22px',
    fontWeight: 600,
    color: '#222222',
    marginBottom: '32px',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
    lineHeight: 1.25,
});

const SectionTitle = styled(Typography)({
    fontSize: '16px',
    fontWeight: 600,
    color: '#222222',
    marginBottom: '24px',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
});

const FieldContainer = styled(Box)({
    width: '100%',
    marginBottom: '24px',
});

const FieldRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #ebebeb',
});

const FieldLabel = styled(Typography)({
    fontSize: '16px',
    color: '#222222',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
});

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        '& fieldset': {
            borderColor: '#b0b0b0',
        },
        '&:hover fieldset': {
            borderColor: '#222222',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#222222',
            borderWidth: '2px',
        },
    },
    '& .MuiInputBase-input': {
        padding: '14px 16px',
        fontSize: '16px',
    },
});

const Footer = styled(Box)({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    borderTop: '1px solid #ebebeb',
    zIndex: 100,
});

const ProgressContainer = styled(Box)({
    display: 'flex',
    gap: '4px',
    padding: '0 40px',
});

const ProgressSegment = styled(Box)<{ active?: boolean; completed?: boolean }>(({ active, completed }) => ({
    flex: 1,
    height: '4px',
    backgroundColor: completed ? '#222222' : active ? '#222222' : '#dddddd',
    transition: 'background-color 0.3s ease',
}));

const FooterActions = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 40px',
});

const BackButton = styled(Button)({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    color: '#222222',
    textDecoration: 'underline',
    padding: '12px 0',
    '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
    },
    '&:disabled': {
        color: '#dddddd',
    },
});

const NextButton = styled(Button)({
    backgroundColor: '#222222',
    color: '#ffffff',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    padding: '14px 24px',
    minWidth: '100px',
    '&:hover': {
        backgroundColor: '#000000',
    },
    '&:disabled': {
        backgroundColor: '#dddddd',
        color: '#ffffff',
    },
});

const StuckCheckbox = styled(FormControlLabel)({
    marginLeft: 0,
    '& .MuiTypography-root': {
        fontSize: '14px',
        color: '#717171',
    },
});

const ReviewCard = styled(Box)({
    backgroundColor: '#f7f7f7',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
});

interface WelcomeQuestionnaireProps {
    previewMode?: boolean;
}

const WelcomeQuestionnaire: React.FC<WelcomeQuestionnaireProps> = ({ previewMode = false }) => {
    const navigate = useNavigate();
    const { user, updateUser, setHasPendingQuestionnaires } = useAuthStore();
    const [sections, setSections] = useState<string[]>([]);
    const [activeSection, setActiveSection] = useState(0);

    const [definition, setDefinition] = useState<QuestionnaireDefinition | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [answers, setAnswers] = useState<Record<string, any>>({});
    const [stuckFields, setStuckFields] = useState<Set<string>>(new Set());
    const [showAiWidget, setShowAiWidget] = useState(false);

    const [assignLawyerDialogOpen, setAssignLawyerDialogOpen] = useState(false);
    const [associatedLawyers, setAssociatedLawyers] = useState<Array<{ id: string; name: string; email: string }>>([]);
    const [assignLawyerLoading, setAssignLawyerLoading] = useState(false);
    const [selectedLawyerId, setSelectedLawyerId] = useState('');
    const [draftSaving, setDraftSaving] = useState(false);
    const [draftMessage, setDraftMessage] = useState('');
    const [draftError, setDraftError] = useState('');

    const handleOpenAssignLawyerDialog = async () => {
        setAssignLawyerLoading(true);
        setAssignLawyerDialogOpen(true);
        try {
            const res = await api.get<Array<{ id: string; name: string; email: string }>>('/lawyers/associated');
            setAssociatedLawyers(res.data || []);
            setSelectedLawyerId(res.data?.[0]?.id || '');
        } catch {
            setAssociatedLawyers([]);
        } finally {
            setAssignLawyerLoading(false);
        }
    };

    // Filter steps for current section
    const currentSectionSteps = definition ? definition.steps.filter(s => s.section === sections[activeSection]) : [];

    // Global step index calculation is tricky with sections.
    // Let's track activeStep within the SECTION.
    const [activeStepInSection, setActiveStepInSection] = useState(0);

    const { id } = useParams<{ id?: string }>();
    const location = useLocation();
    const queryParamId = new URLSearchParams(location.search).get('questionnaireId') || new URLSearchParams(location.search).get('id');
    const targetQuestionnaireId = id || queryParamId;
    const ownerId = new URLSearchParams(location.search).get('ownerId') || undefined;
    const isLawyerReview = Boolean(ownerId && user?.userType === 'EL');
    const requestedAssignmentId = new URLSearchParams(location.search).get('assignmentId') || undefined;
    const draftPayload = () => ({
        answers, questionnaireVersion: definition?.version || 1,
        assignmentId: definition?.assignmentId, ownerId
    });
    const saveDraft = async () => {
        if (previewMode || !definition) return;
        setDraftSaving(true);
        setDraftError('');
        try {
            await api.put('/welcome-questionnaire/draft', draftPayload());
            setDraftMessage('Draft saved. Your associated lawyer can review it.');
        } catch {
            setDraftError('Could not save the draft. Please try again.');
            throw new Error('Draft save failed');
        } finally { setDraftSaving(false); }
    };
    const assignDraft = async () => {
        setDraftSaving(true);
        setDraftError('');
        try {
            await api.post('/welcome-questionnaire/draft/assign-lawyer', { ...draftPayload(), lawyerId: selectedLawyerId });
            setDraftMessage('Draft saved and assigned to your lawyer.');
            setAssignLawyerDialogOpen(false);
        } catch { setDraftError('Could not assign the draft. Please try again.'); }
        finally { setDraftSaving(false); }
    };

    useEffect(() => {
        fetchDefinition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewMode, targetQuestionnaireId, ownerId, requestedAssignmentId]);

    const fetchDefinition = async () => {
        try {
            if (targetQuestionnaireId) {
                const res = await api.get<any>(`/questionnaires/${targetQuestionnaireId}`);
                setDefinition({
                    version: res.data.version || 1,
                    steps: res.data.steps || [],
                    isAssigned: true,
                    title: res.data.title,
                    description: res.data.description,
                });
                const uniqueSections = Array.from(new Set((res.data.steps || []).map((s: any) => s.section || 'General')));
                setSections(uniqueSections as string[]);
                return;
            }

            const endpoint = previewMode
                ? '/welcome-questionnaire/definition/admin'
                : `/welcome-questionnaire/definition?${new URLSearchParams({ ...(ownerId ? { ownerId } : {}), ...(requestedAssignmentId ? { assignmentId: requestedAssignmentId } : {}) })}`;
            const res = await api.get<QuestionnaireDefinition>(endpoint);
            setDefinition(res.data);
            setAnswers(res.data.answers || {});
            if (new URLSearchParams(location.search).get('assignLawyer') === '1') void handleOpenAssignLawyerDialog();
            // Extract unique sections
            const uniqueSections = Array.from(new Set(res.data.steps.map(s => s.section || 'General')));
            setSections(uniqueSections);
        } catch (err: any) {
            if (err?.response?.status === 404 && err?.response?.data?.hasCompletedAssignedQuestionnaires) {
                updateUser({ welcomeQuestionnaireCompleted: true });
                useAuthStore.getState().setHasPendingQuestionnaires(false, 0);
                navigate('/');
                return;
            }
            setError('Could not load questionnaire preview. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = async () => {
        if (!previewMode) {
            try { await saveDraft(); } catch { return; }
        }
        if (validateStep(activeStepInSection)) {
            if (activeStepInSection < currentSectionSteps.length - 1) {
                // Next step in current section
                setActiveStepInSection(prev => prev + 1);
            } else if (activeSection < sections.length - 1) {
                // Next section
                setActiveSection(prev => prev + 1);
                setActiveStepInSection(0);
            } else if (previewMode || isLawyerReview) {
                if (isLawyerReview) { navigate('/assigned-documents'); }
                else if (window.history.length > 1) {
                    navigate(-1);
                } else {
                    navigate('/questionnaire-library');
                }
            } else {
                // Final submission
                handleSubmit();
            }
        }
    };

    const handleBack = () => {
        if (activeStepInSection > 0) {
            setActiveStepInSection(prev => prev - 1);
        } else if (activeSection > 0) {
            setActiveSection(prev => prev - 1);
            // Go to last step of previous section
            const prevSection = sections[activeSection - 1];
            const prevSteps = definition?.steps.filter(s => s.section === prevSection) || [];
            setActiveStepInSection(prevSteps.length - 1);
        }
    };

    const validateStep = (stepIndex: number) => {
        if (!definition) return false;
        // Find the actual step object based on current section and local index
        const step = currentSectionSteps[stepIndex];
        let isValid = true;

        for (const field of step.fields) {
            const isStuck = stuckFields.has(field.id);
            const value = answers[field.id];

            // Required check
            if (field.required && !isStuck) {
                if (field.type === 'checkbox') {
                    // Boolean check
                    if (!value) isValid = false;
                } else if (field.type !== 'display' && field.type !== 'file' && (!value || value === '')) {
                    // File is optional validation for now unless we implement real upload check
                    isValid = false;
                } else if (field.type === 'file' && field.required && !value) {
                    // Check file value (base64)
                    isValid = false;
                }
            }
        }

        if (!isValid) {
            alert('Please fill in all required fields or mark them as "I am stuck".');
        }
        return isValid;
    };

    const handleSubmit = async () => {
        if (!definition || previewMode) return;

        // Final validation of last step
        // (Handled by handleNext calls usually, but let's double check if called directly?)
        // handleNext calls handleSubmit only if valid.

        try {
            const stuckArray = Array.from(stuckFields).map(key => {
                let fieldDef: Field | undefined;
                let sectionTitle: string | undefined;
                definition.steps.forEach((s: StepDef) => s.fields.forEach((f: Field) => {
                    if (f.id === key) {
                        fieldDef = f;
                        sectionTitle = s.title;
                    }
                }));
                return {
                    key,
                    label: fieldDef?.label,
                    type: fieldDef?.type,
                    sectionTitle
                };
            });

            let createdDocId: string | undefined;
            // Check if this is a lawyer-assigned questionnaire
            if (definition.isAssigned && definition.assignmentId) {
                // Submit to the assignment-specific endpoint (include stuck fields)
                const res = await api.post(`/questionnaires/user/assignments/${definition.assignmentId}/responses`, {
                    responses: answers,
                    isComplete: true,
                    stuckFields: stuckArray
                });
                createdDocId = res.data?.documentId;

                const pending = await userQuestionnaireService.checkPending();
                updateUser({
                    welcomeQuestionnaireCompleted: true,
                    hasPendingQuestionnaires: pending.hasPending
                });
                setHasPendingQuestionnaires(pending.hasPending, pending.pendingCount);
            } else {
                // Submit to the default welcome questionnaire endpoint
                const res = await api.post('/welcome-questionnaire/submit', {
                    answers,
                    stuckFields: stuckArray,
                    questionnaireVersion: definition.version,
                    definitionId: definition._id || definition.id,
                });
                createdDocId = res.data?.documentId;

                const pending = await userQuestionnaireService.checkPending();
                updateUser({
                    welcomeQuestionnaireCompleted: true,
                    hasPendingQuestionnaires: pending.hasPending
                });
                setHasPendingQuestionnaires(pending.hasPending, pending.pendingCount);
            }

            if (createdDocId) {
                navigate(`/document-library/documents/${createdDocId}`);
            } else {
                navigate('/document-library');
            }

        } catch (err) {
            setError('Failed to submit. Please try again.');
        }
    };

    const handleFieldChange = (fieldId: string, value: any) => {
        setAnswers(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleFileChange = (fieldId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            // Convert to Base64
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                handleFieldChange(fieldId, base64String); // Store base64 string
            };
            reader.readAsDataURL(file);
        }
    };

    const toggleStuck = (fieldId: string) => {
        setStuckFields(prev => {
            const next = new Set(prev);
            if (next.has(fieldId)) {
                next.delete(fieldId);
            } else {
                next.add(fieldId);
                handleFieldChange(fieldId, '');
            }
            return next;
        });
    };

    if (loading) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress sx={{ color: '#222222' }} />
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Alert severity="error" sx={{ maxWidth: 400 }}>{error}</Alert>
            </PageContainer>
        );
    }

    if (!definition) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Alert severity="warning">No questionnaire definition found.</Alert>
            </PageContainer>
        );
    }

    const activeStepDef = currentSectionSteps[activeStepInSection];

    if (!activeStepDef) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress sx={{ color: '#222222' }} />
            </PageContainer>
        );
    }

    const isFirstStep = activeStepInSection === 0 && activeSection === 0;
    const isLastStep = activeSection === sections.length - 1 && activeStepInSection === currentSectionSteps.length - 1;

    return (
        <PageContainer>
            {/* Header */}
            <Header>
                <Logo>{definition?.isAssigned && definition?.title ? definition.title : 'Wills'}</Logo>
                <HeaderActions>
                    {previewMode && (
                        <HeaderButton
                            variant="outlined"
                            onClick={() => {
                                if (window.history.length > 1) {
                                    navigate(-1);
                                } else {
                                    navigate('/questionnaire-library');
                                }
                            }}
                        >
                            Exit Preview
                        </HeaderButton>
                    )}
                    {!previewMode && (
                        <>
                            <HeaderButton variant="outlined" disabled={draftSaving} onClick={() => { void saveDraft().catch(() => undefined); }}>
                                {draftSaving ? 'Saving…' : 'Save Draft'}
                            </HeaderButton>
                            <HeaderButton onClick={async () => {
                                try { await saveDraft(); navigate(isLawyerReview ? '/assigned-documents' : '/document-library'); } catch { /* Keep unsaved answers on screen. */ }
                            }} disabled={draftSaving}>Save and Exit</HeaderButton>
                        </>
                    )}
                    {!previewMode && user?.userType === 'EO' && (
                        <HeaderButton
                            variant="outlined"
                            startIcon={<PersonAddIcon />}
                            onClick={handleOpenAssignLawyerDialog}
                        >
                            Assign to Lawyer
                        </HeaderButton>
                    )}
                    <HeaderButton
                        variant="outlined"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setShowAiWidget(true)}
                    >
                        Questions?
                    </HeaderButton>

                </HeaderActions>
            </Header>

            {/* Main Content */}
            <MainContent>
                {/* Page Title (Section Name) */}
                <PageTitle>
                    {definition?.isAssigned && definition?.title
                        ? definition.title
                        : (sections[activeSection] || 'Welcome to Your Will Setup')}
                </PageTitle>

                {/* Section Title (Step Title) */}
                <Box sx={{ width: '100%' }}>
                    <SectionTitle>
                        {activeStepDef.title}
                    </SectionTitle>

                    {activeStepDef.description && (
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: '#717171',
                                marginBottom: '24px',
                            }}
                        >
                            {activeStepDef.description}
                        </Typography>
                    )}

                    {/* Auto-generated Review Summary for Review Steps */}
                    {activeStepDef.title.toLowerCase().includes('review') && (
                        <ReviewCard sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '12px', bgcolor: '#fafafa' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: '#717171',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.75px',
                                        fontWeight: 700,
                                    }}
                                >
                                    Summary of {sections[activeSection]}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#717171' }}>
                                    Review your answers before proceeding
                                </Typography>
                            </Box>
                            {currentSectionSteps
                                .filter(s => s !== activeStepDef)
                                .map(step => {
                                    const stepOriginalIndex = currentSectionSteps.indexOf(step);
                                    return (
                                        <Box
                                            key={step.title}
                                            sx={{
                                                mb: 2.5,
                                                p: 2,
                                                bgcolor: '#ffffff',
                                                borderRadius: '8px',
                                                border: '1px solid #ebebeb',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '15px',
                                                        fontWeight: 600,
                                                        color: '#222222',
                                                    }}
                                                >
                                                    {step.title}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setActiveStepInSection(stepOriginalIndex)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        color: '#222222',
                                                        borderColor: '#222222',
                                                        borderRadius: '16px',
                                                        px: 1.5,
                                                        py: 0.25,
                                                        '&:hover': {
                                                            bgcolor: '#f5f5f5',
                                                            borderColor: '#000000',
                                                        },
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {step.fields.map(f => {
                                                    const val = answers[f.id];
                                                    if (f.type === 'display') return null;
                                                    let displayVal = '-';
                                                    if (f.type === 'checkbox') {
                                                        displayVal = val ? 'Yes' : 'No';
                                                    } else if (Array.isArray(val)) {
                                                        displayVal = val.join(', ') || '-';
                                                    } else if (val) {
                                                        displayVal = String(val);
                                                    }

                                                    return (
                                                        <Box
                                                            key={f.id}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'baseline',
                                                                py: 0.5,
                                                                borderBottom: '1px dashed #f0f0f0',
                                                                '&:last-child': { borderBottom: 'none' }
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    color: '#717171',
                                                                    fontWeight: 500,
                                                                    maxWidth: '55%',
                                                                }}
                                                            >
                                                                {f.label}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    color: '#222222',
                                                                    fontWeight: 600,
                                                                    textAlign: 'right',
                                                                    maxWidth: '40%',
                                                                    wordBreak: 'break-word',
                                                                }}
                                                            >
                                                                {displayVal}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                })
                            }
                        </ReviewCard>
                    )}

                    {/* Fields */}
                    <FieldContainer>
                        {activeStepDef.fields.map(field => (
                            <Box key={field.id} sx={{ marginBottom: '24px' }}>
                                {/* Field with row layout for simple types */}
                                {field.type === 'text' || field.type === 'number' || field.type === 'email' || field.type === 'tel' || field.type === 'date' ? (
                                    <Box>
                                        <FieldRow sx={{ borderBottom: 'none', paddingBottom: '8px' }}>
                                            <FieldLabel>
                                                {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                                            </FieldLabel>
                                            {field.tooltip && (
                                                <Tooltip title={field.tooltip} arrow placement="top">
                                                    <IconButton size="small" sx={{ color: '#717171', ml: 0.5 }}>
                                                        <HelpOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </FieldRow>

                                        {!stuckFields.has(field.id) && (
                                            <StyledTextField
                                                fullWidth
                                                type={field.type}
                                                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                                                value={answers[field.id] || ''}
                                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                                disabled={stuckFields.has(field.id)}
                                                variant="outlined"
                                            />
                                        )}

                                        <StuckCheckbox
                                            control={
                                                <Checkbox
                                                    checked={stuckFields.has(field.id)}
                                                    onChange={() => toggleStuck(field.id)}
                                                    sx={{
                                                        color: '#717171',
                                                        '&.Mui-checked': { color: '#222222' },
                                                    }}
                                                />
                                            }
                                            label="I need help with this"
                                        />

                                        {field.helperText && (
                                            <Typography sx={{ fontSize: '12px', color: '#717171', marginTop: '4px' }}>
                                                {field.helperText}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : field.type === 'textarea' ? (
                                    <Box>
                                        <FieldRow sx={{ borderBottom: 'none', paddingBottom: '8px' }}>
                                            <FieldLabel>
                                                {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                                            </FieldLabel>
                                            {field.aiHelper && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => alert(`AI Tip: ${field.aiHelper}`)}
                                                    sx={{ color: '#717171' }}
                                                >
                                                    <HelpOutlineIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </FieldRow>

                                        {!stuckFields.has(field.id) && (
                                            <StyledTextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                placeholder={field.placeholder}
                                                value={answers[field.id] || ''}
                                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                                disabled={stuckFields.has(field.id)}
                                            />
                                        )}

                                        <StuckCheckbox
                                            control={
                                                <Checkbox
                                                    checked={stuckFields.has(field.id)}
                                                    onChange={() => toggleStuck(field.id)}
                                                    sx={{
                                                        color: '#717171',
                                                        '&.Mui-checked': { color: '#222222' },
                                                    }}
                                                />
                                            }
                                            label="I need help with this"
                                        />
                                    </Box>
                                ) : field.type === 'select' ? (
                                    <Box>
                                        <FieldLabel sx={{ marginBottom: '16px' }}>
                                            {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                                        </FieldLabel>

                                        {!stuckFields.has(field.id) && (
                                            <RadioGroup
                                                value={answers[field.id] || ''}
                                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                            >
                                                {field.options?.map(opt => (
                                                    <FormControlLabel
                                                        key={opt}
                                                        value={opt}
                                                        control={
                                                            <Radio
                                                                sx={{
                                                                    color: '#b0b0b0',
                                                                    '&.Mui-checked': { color: '#222222' },
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography sx={{ fontSize: '16px', color: '#222222' }}>
                                                                {opt}
                                                            </Typography>
                                                        }
                                                        sx={{ marginBottom: '8px' }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}

                                        <StuckCheckbox
                                            control={
                                                <Checkbox
                                                    checked={stuckFields.has(field.id)}
                                                    onChange={() => toggleStuck(field.id)}
                                                    sx={{
                                                        color: '#717171',
                                                        '&.Mui-checked': { color: '#222222' },
                                                    }}
                                                />
                                            }
                                            label="I need help with this"
                                        />
                                    </Box>
                                ) : field.type === 'checkbox' ? (
                                    <Box>
                                        <FieldLabel sx={{ marginBottom: '16px' }}>
                                            {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                                        </FieldLabel>

                                        {!stuckFields.has(field.id) && (
                                            <RadioGroup
                                                value={answers[field.id] === true ? 'yes' : answers[field.id] === false ? 'no' : ''}
                                                onChange={(e) => handleFieldChange(field.id, e.target.value === 'yes')}
                                            >
                                                <FormControlLabel
                                                    value="yes"
                                                    control={
                                                        <Radio
                                                            sx={{
                                                                color: '#b0b0b0',
                                                                '&.Mui-checked': { color: '#222222' },
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '16px', color: '#222222' }}>Yes</Typography>}
                                                    sx={{ marginBottom: '8px' }}
                                                />
                                                <FormControlLabel
                                                    value="no"
                                                    control={
                                                        <Radio
                                                            sx={{
                                                                color: '#b0b0b0',
                                                                '&.Mui-checked': { color: '#222222' },
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontSize: '16px', color: '#222222' }}>No</Typography>}
                                                />
                                            </RadioGroup>
                                        )}

                                        <StuckCheckbox
                                            control={
                                                <Checkbox
                                                    checked={stuckFields.has(field.id)}
                                                    onChange={() => toggleStuck(field.id)}
                                                    sx={{
                                                        color: '#717171',
                                                        '&.Mui-checked': { color: '#222222' },
                                                    }}
                                                />
                                            }
                                            label="I need help with this"
                                        />
                                    </Box>
                                ) : field.type === 'display' ? (
                                    <Box
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            borderRadius: '12px',
                                            padding: '20px',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                color: '#222222',
                                                whiteSpace: 'pre-line',
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {field.placeholder || field.label}
                                        </Typography>
                                    </Box>
                                ) : field.type === 'file' ? (
                                    <Box>
                                        <FieldLabel sx={{ marginBottom: '16px' }}>
                                            {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                                        </FieldLabel>

                                        {!stuckFields.has(field.id) && (
                                            <Box
                                                sx={{
                                                    border: '2px dashed #b0b0b0',
                                                    borderRadius: '12px',
                                                    padding: '32px',
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'border-color 0.2s',
                                                    '&:hover': {
                                                        borderColor: '#222222',
                                                    },
                                                }}
                                            >
                                                <input
                                                    accept="application/pdf,image/*"
                                                    type="file"
                                                    id={`file-${field.id}`}
                                                    onChange={(e) => handleFileChange(field.id, e)}
                                                    disabled={stuckFields.has(field.id)}
                                                    style={{ display: 'none' }}
                                                />
                                                <label htmlFor={`file-${field.id}`} style={{ cursor: 'pointer' }}>
                                                    <Typography sx={{ color: '#717171', fontSize: '14px' }}>
                                                        {answers[field.id] ? 'File selected ✓' : 'Click to upload or drag and drop'}
                                                    </Typography>
                                                </label>
                                            </Box>
                                        )}

                                        <StuckCheckbox
                                            control={
                                                <Checkbox
                                                    checked={stuckFields.has(field.id)}
                                                    onChange={() => toggleStuck(field.id)}
                                                    sx={{
                                                        color: '#717171',
                                                        '&.Mui-checked': { color: '#222222' },
                                                    }}
                                                />
                                            }
                                            label="I need help with this"
                                        />
                                    </Box>
                                ) : (
                                    // Default text field
                                    <Box>
                                        <FieldRow sx={{ borderBottom: 'none', paddingBottom: '8px' }}>
                                            <FieldLabel>
                                                {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                                            </FieldLabel>
                                        </FieldRow>

                                        {!stuckFields.has(field.id) && (
                                            <StyledTextField
                                                fullWidth
                                                type={field.type}
                                                value={answers[field.id] || ''}
                                                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                                disabled={stuckFields.has(field.id)}
                                            />
                                        )}

                                        <StuckCheckbox
                                            control={
                                                <Checkbox
                                                    checked={stuckFields.has(field.id)}
                                                    onChange={() => toggleStuck(field.id)}
                                                    sx={{
                                                        color: '#717171',
                                                        '&.Mui-checked': { color: '#222222' },
                                                    }}
                                                />
                                            }
                                            label="I need help with this"
                                        />
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </FieldContainer>
                </Box>
            </MainContent>

            {/* Footer with Progress Bar */}
            <Footer>
                <ProgressContainer>
                    {sections.map((_, idx) => (
                        <ProgressSegment
                            key={idx}
                            active={idx === activeSection}
                            completed={idx < activeSection}
                        />
                    ))}
                </ProgressContainer>
                <FooterActions>
                    <BackButton
                        onClick={handleBack}
                        disabled={isFirstStep}
                        disableRipple
                    >
                        Back
                    </BackButton>
                    <NextButton onClick={handleNext}>
                        {isLastStep ? (previewMode ? 'Close Preview' : 'Submit') : 'Next'}
                    </NextButton>
                </FooterActions>
            </Footer>

            {draftMessage && <Alert severity="success">{draftMessage}</Alert>}
            {draftError && <Alert severity="error">{draftError}</Alert>}
            {/* Assign / Share with Lawyer Dialog */}
            <Dialog
                open={assignLawyerDialogOpen}
                onClose={() => setAssignLawyerDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Assigned Estate Lawyer</DialogTitle>
                <DialogContent dividers>
                    {assignLawyerLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : associatedLawyers.length === 0 ? (
                        <Alert severity="info">
                            You do not have an associated lawyer yet.
                            <Button onClick={async () => { try { await saveDraft(); navigate('/lawyer-management'); } catch { /* Keep the draft open. */ } }}>Save Draft and Manage Lawyers</Button>
                        </Alert>
                    ) : (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Save your current answers and assign this draft for review. You can continue editing afterward.
                            </Typography>
                            <TextField select fullWidth label="Estate Lawyer" value={selectedLawyerId}
                                onChange={event => setSelectedLawyerId(event.target.value)}>
                                {associatedLawyers.map(lawyer => <MenuItem key={lawyer.id} value={lawyer.id}>
                                    {lawyer.name || lawyer.email}
                                </MenuItem>)}
                            </TextField>
                            {draftError && <Alert severity="error">{draftError}</Alert>}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignLawyerDialogOpen(false)}>Close</Button>
                    <Button onClick={assignDraft} disabled={draftSaving || !selectedLawyerId}>Save and Assign</Button>
                </DialogActions>
            </Dialog>

            <AiHelperWidget
                isWelcomeQuestionnaire
                isOpen={showAiWidget}
                onClose={() => setShowAiWidget(false)}
                hideFab
            />
        </PageContainer>
    );
};

export default WelcomeQuestionnaire;
